import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  CreditCard, 
  Users, 
  Settings, 
  LogOut,
  Clock,
  ClipboardList,
  Shield,
  Package,
  Building2,
  BookOpen,
  HelpCircle
} from 'lucide-react';

const SuperAdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/superadmin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/superadmin/orders', label: 'Orders', icon: FileText },
    { path: '/superadmin/companies', label: 'Companies', icon: Building2 },
    { path: '/superadmin/payments', label: 'Payments', icon: CreditCard },
    { path: '/superadmin/products', label: 'Products', icon: Package },
    { path: '/superadmin/users', label: 'Users', icon: Users },
    { path: '/superadmin/scheduler', label: 'Scheduler', icon: Clock },
    { path: '/superadmin/blog', label: 'Blog', icon: BookOpen },
    { path: '/superadmin/faq', label: 'FAQ', icon: HelpCircle },
    { path: '/superadmin/audit', label: 'Audit Logs', icon: ClipboardList },
    { path: '/superadmin/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0D1117' }}>
      {/* Header */}
      <header className="border-b border-gray-800 sticky top-0 z-50" style={{ backgroundColor: '#0D1117' }}>
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/superadmin" className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">StartEase Agent</h1>
            <span 
              className="text-xs font-semibold px-2 py-1 rounded flex items-center gap-1"
              style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}
            >
              <Shield className="w-3 h-3" />
              Super Admin
            </span>
          </Link>
          
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-400 hidden sm:block">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-gray-800 min-h-[calc(100vh-65px)] hidden md:block" style={{ backgroundColor: '#0D1117' }}>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    active 
                      ? 'bg-[#00E87A]/10 text-[#00E87A]' 
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Bottom Nav */}
        <nav className="fixed bottom-0 left-0 right-0 border-t border-gray-800 md:hidden z-50" style={{ backgroundColor: '#0D1117' }}>
          <div className="flex justify-around py-2">
            {navItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 px-3 py-2 ${
                    active ? 'text-[#00E87A]' : 'text-gray-400'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-6 pb-24 md:pb-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
